import { useCallback, useEffect, useMemo, useState } from "react";
import {
  billingFileName,
  buildBillingCsv,
  formatMoney,
  todayLocal,
  type BillingLine,
  type ExportTemplate,
} from "@iltocat/shared";
import { ApiError, api, plural } from "../lib/api.js";
import { downloadBlob } from "../lib/download.js";
import { calendarDay } from "../lib/stockFormat.js";
import { EmptyState } from "../components/EmptyState.js";
import { Icon } from "../components/Icon.js";
import { Notice } from "../components/Notice.js";
import { PageHeader } from "../components/PageHeader.js";
import { Panel } from "../components/Panel.js";
import { TemplateEditor } from "../components/TemplateEditor.js";
import { useToast } from "../components/Toast.js";

/**
 * Month end: everything deployed, billable and not yet billed, up to a date,
 * as a CSV for whatever billing system the company already uses. **Not**
 * in-app invoicing -- numbering, tax and payments are a product of their own
 * (the roadmap's words), so this ends at a file.
 *
 * Download first, then mark billed: the two are separate on purpose, so a
 * file that the billing system rejects can be fixed and downloaded again
 * without the lines having already vanished from this list.
 *
 * The CSV is built in the browser from the lines on screen, by the same pure
 * function the API suite checks against the exit gate's scenario.
 */
export function Billing() {
  const [through, setThrough] = useState(todayLocal());
  const [lines, setLines] = useState<BillingLine[] | null>(null);
  const [templates, setTemplates] = useState<ExportTemplate[]>([]);
  const [templateId, setTemplateId] = useState("builtin-generic");
  const [markup, setMarkup] = useState("");
  const [downloaded, setDownloaded] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  const loadLines = useCallback(async (date: string) => {
    setLines(null);
    setDownloaded(false);
    try {
      setLines((await api.billing.unbilled(date)).lines);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not load the billing lines.");
      setLines([]);
    }
  }, []);

  useEffect(() => {
    void loadLines(through);
  }, [loadLines, through]);

  useEffect(() => {
    void Promise.all([api.billing.templates(), api.stock.settings()])
      .then(([list, settings]) => {
        setTemplates(list.templates);
        setMarkup(String(settings.settings.markupPercent));
      })
      .catch(() => setError("Could not load the export templates."));
  }, []);

  const template = templates.find((one) => one.id === templateId) ?? templates[0];

  const byClient = useMemo(() => {
    const groups = new Map<string, BillingLine[]>();
    for (const line of lines ?? [])
      groups.set(line.clientName, [...(groups.get(line.clientName) ?? []), line]);
    return [...groups.entries()];
  }, [lines]);

  const total = (rows: BillingLine[]) =>
    rows.reduce((sum, line) => sum + line.unitPriceCents * line.quantity, 0);

  function download() {
    if (!lines || !template) return;
    const exportedOn = todayLocal();
    const csv = buildBillingCsv(lines, template, exportedOn);
    downloadBlob(
      new Blob([csv], { type: "text/csv;charset=utf-8" }),
      billingFileName(template.name, exportedOn),
    );
    setDownloaded(true);
  }

  async function markBilled() {
    if (!lines?.length) return;
    const ok = window.confirm(
      `Mark ${plural(lines.length, "line")} as billed? They leave this list for good. Do it once the file is in your billing system.`,
    );
    if (!ok) return;
    setBusy("mark");
    setError(null);
    try {
      const { marked } = await api.billing.mark(lines.map((line) => line.deploymentId));
      toast(`Marked ${plural(marked, "deployment")} as billed.`);
      await loadLines(through);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Those were not marked.");
    } finally {
      setBusy(null);
    }
  }

  async function saveMarkup(event: React.FormEvent) {
    event.preventDefault();
    setBusy("markup");
    try {
      const { settings } = await api.stock.saveSettings({ markupPercent: Number(markup) || 0 });
      setMarkup(String(settings.markupPercent));
      toast(`New deployments start at cost plus ${settings.markupPercent}%.`);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "That did not save.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <PageHeader title="Billing" />
      {error && <Notice tone="error">{error}</Notice>}

      <Panel
        title="Waiting to be billed"
        note="Everything deployed and billable that has not been marked billed, up to the date you pick. Units that came back before billing are left off."
      >
        <label className="billing-through">
          <span className="field-label">Deployed up to</span>
          <input
            className="input input-small"
            type="date"
            value={through}
            onChange={(event) => event.target.value && setThrough(event.target.value)}
          />
        </label>
        {lines === null ? (
          <div className="skeleton-line" />
        ) : lines.length === 0 ? (
          <EmptyState icon="check-circle" title="Nothing waiting to be billed." />
        ) : (
          <>
            {byClient.map(([client, rows]) => (
              <section key={client} className="billing-client">
                <h3 className="facts-title">
                  {client} <span className="muted">· {formatMoney(total(rows))}</span>
                </h3>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Went out</th>
                      <th>Item</th>
                      <th>Serial</th>
                      <th>Units</th>
                      <th>Each</th>
                      <th>Line</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((line) => (
                      <tr key={`${line.deploymentId}-${line.kind}`}>
                        <td data-label="Went out">{calendarDay(line.deployedOn)}</td>
                        <td data-label="Item">{line.title || "Untitled item"}</td>
                        <td data-label="Serial" className="mono">
                          {line.serial}
                        </td>
                        <td data-label="Units">{line.quantity}</td>
                        <td data-label="Each">{formatMoney(line.unitPriceCents)}</td>
                        <td data-label="Line">{formatMoney(line.unitPriceCents * line.quantity)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            ))}

            <div className="billing-export">
              <label className="field">
                <span className="field-label">Export as</span>
                <select
                  className="input"
                  value={template?.id ?? ""}
                  onChange={(event) => setTemplateId(event.target.value)}
                >
                  {templates.map((one) => (
                    <option key={one.id} value={one.id}>
                      {one.name}
                    </option>
                  ))}
                </select>
              </label>
              {template?.builtIn && template.note && (
                <Notice tone={template.verified ? "ok" : "warn"}>{template.note}</Notice>
              )}
              <div className="panel-foot">
                <span className="muted">
                  {plural(lines.length, "line")} · {formatMoney(total(lines))}
                </span>
                <button type="button" className="button button-quiet" onClick={download}>
                  <Icon name="download" size={18} />
                  Download CSV
                </button>
                <button
                  type="button"
                  className={downloaded ? "button" : "button button-quiet"}
                  disabled={busy === "mark"}
                  onClick={() => void markBilled()}
                >
                  <Icon name="check" size={18} />
                  Mark as billed
                </button>
              </div>
            </div>
          </>
        )}
      </Panel>

      <Panel
        title="Pricing"
        note="A deployment's price starts at cost plus this, and can be typed over line by line."
      >
        <form className="inline-add" onSubmit={saveMarkup}>
          <label className="field-label" htmlFor="markup">
            Standard markup
          </label>
          <input
            id="markup"
            className="input input-number"
            type="number"
            min={0}
            max={1000}
            value={markup}
            onChange={(event) => setMarkup(event.target.value)}
          />
          <span className="muted">%</span>
          <button type="submit" className="button" disabled={busy === "markup"}>
            Save
          </button>
        </form>
      </Panel>

      <TemplateEditor
        templates={templates}
        onSaved={(next, name) => {
          setTemplates(next);
          const saved = next.find((one) => !one.builtIn && one.name.toLowerCase() === name.toLowerCase());
          if (saved) setTemplateId(saved.id);
        }}
      />
    </>
  );
}
